import { useEffect, useRef, useState } from "react";
import { api, BRL, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Upload, Star, Trash2, Send, Bot, ArrowRightLeft, StickyNote, ImageOff, Save, Loader2, ShoppingCart, AlertTriangle, Printer } from "lucide-react";

const ACT_ICON = { ai: Bot, stage_change: ArrowRightLeft, note: StickyNote, print: Printer };

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function OpportunityDetail({ oppId, stages = [], onClose, onUpdated, productionItem = null, productionStages = [], onRequestPrint = null }) {
  const [opp, setOpp] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({});
  const [prodStage, setProdStage] = useState(productionItem?.stage || "");
  const [prodDue, setProdDue] = useState(productionItem?.due_date || "");
  const todayISO = new Date().toISOString().slice(0, 10);
  useEffect(() => { setProdDue(productionItem?.due_date || ""); setProdStage(productionItem?.stage || ""); }, [productionItem?.id, productionItem?.due_date, productionItem?.stage]);
  const printInfo = () => {
    if (!productionItem) return null;
    const printed = !!productionItem.last_printed_at;
    const dirty = productionItem.print_dirty !== false;
    const isRed = !printed || dirty;
    let tip;
    if (!printed) tip = "Nunca foi impresso";
    else if (dirty) tip = "Alterado após a última impressão — reimprimir";
    else tip = `Impresso em ${new Date(productionItem.last_printed_at).toLocaleString("pt-BR")}${productionItem.last_printed_by_name ? ` por ${productionItem.last_printed_by_name}` : ""}`;
    return { isRed, tip };
  };
  const fileRef = useRef(null);
  const [grid, setGrid] = useState([]);
  const sizesFor = (m) => (m === "INF" ? ["2","4","6","8","10","12","14","16"] : ["PP","P","M","G","GG","EG","G1","G2","G3"]);
  const rowDone = (r) => Number(r.qty) > 0 && r.modelagem && r.tamanho;
  const setRow = (i, k, v) => setGrid((g) => g.map((r, ix) => ix === i ? { ...r, [k]: v, ...(k === "modelagem" ? { tamanho: "" } : {}) } : r));
  const addRow = () => setGrid((g) => [...g, { qty: "", modelagem: "", tamanho: "" }]);
  const rmRow = (i) => setGrid((g) => g.filter((_, ix) => ix !== i));

  const load = async () => {
    const { data } = await api.get(`/opportunities/${oppId}`);
    setOpp(data);
    setForm({
      title: data.title || "", product: data.product || "", quantity: data.quantity || 0,
      estimated_value: data.estimated_value || 0, owner_id: data.owner_id || "", notes: data.notes || "",
    });
    setGrid(data.size_grid?.length ? data.size_grid : [{ qty: "", modelagem: "", tamanho: "" }]);
  };

  useEffect(() => {
    if (!oppId) return;
    load();
    api.get("/sellers").then(({ data }) => setSellers(data));
  }, [oppId]);

  const refresh = async () => { await load(); onUpdated && onUpdated(); };

  const gridTotal = grid.reduce((a, b) => a + (Number(b.qty) || 0), 0);
  const qtyNum = Number(form.quantity) || 0;
  const qtyMismatch = gridTotal > 0 && qtyNum > 0 && qtyNum !== gridTotal;
  const effectiveQty = qtyNum > 0 ? qtyNum : gridTotal;
  const qtyMissing = effectiveQty <= 0;

  // Auto-preenche a Qtde quando vazia/zerada conforme a grade de tamanhos
  useEffect(() => {
    const total = grid.reduce((a, b) => a + (Number(b.qty) || 0), 0);
    if (total > 0 && (form.quantity === "" || form.quantity === 0 || form.quantity === "0" || form.quantity == null)) {
      setForm((f) => ({ ...f, quantity: total }));
    }
  }, [grid]);

  const notifyReprint = (resp) => {
    if (resp?.needs_reprint_alert && onRequestPrint) {
      toast.warning("Este pedido foi alterado. Lembre-se de reimprimir.", {
        action: { label: "Imprimir agora", onClick: () => onRequestPrint() },
        duration: 6000,
      });
    }
  };

  const saveFields = async () => {
    const total = grid.reduce((a, b) => a + (Number(b.qty) || 0), 0);
    const q = Number(form.quantity) || 0;
    let finalQty = q;
    if (total > 0) {
      if (q === 0) {
        finalQty = total;
      } else if (q !== total) {
        toast.error(`Qtde (${q}) diferente do total da grade (${total}). Corrija antes de salvar.`);
        return;
      }
    }
    setSaving(true);
    try {
      if (finalQty <= 0) { toast.error("Informe a quantidade (obrigatório)."); setSaving(false); return; }
      const { data } = await api.patch(`/opportunities/${oppId}`, {
        ...form, quantity: finalQty, estimated_value: Number(form.estimated_value) || 0,
        owner_id: form.owner_id || null, size_grid: grid.filter(rowDone),
      });
      if (finalQty !== q) setForm((f) => ({ ...f, quantity: finalQty }));
      toast.success("Oportunidade atualizada");
      notifyReprint(data);
      await refresh();
    } catch (e) { toast.error("Erro ao salvar"); } finally { setSaving(false); }
  };

  const moveStage = async (stage_key) => {
    await api.patch(`/opportunities/${oppId}/stage`, { stage_key });
    toast.success("Etapa alterada");
    await refresh();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/opportunities/${oppId}/activities`, { text: note.trim() });
    setNote(""); await load();
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/opportunities/${oppId}/images`, fd);
      toast.success("Foto enviada ao Drive do cliente");
      notifyReprint(data);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const setCover = async (image_id) => {
    const { data } = await api.patch(`/opportunities/${oppId}`, { cover_image_id: image_id });
    notifyReprint(data);
    await refresh();
  };
  const delImage = async (image_id) => {
    const { data } = await api.delete(`/opportunities/${oppId}/images/${image_id}`);
    toast.success("Foto removida");
    notifyReprint(data);
    await refresh();
  };

  const [tinyLoading, setTinyLoading] = useState(false);
  const pushTiny = async () => {
    setTinyLoading(true);
    try {
      await api.post(`/opportunities/${oppId}/push-tiny`);
      toast.success("Pedido enviado ao Tiny"); await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar ao Tiny"); }
    finally { setTinyLoading(false); }
  };
  const checkTiny = async () => {
    setTinyLoading(true);
    try {
      await api.get(`/opportunities/${oppId}/tiny-status`);
      toast.success("Status atualizado"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setTinyLoading(false); }
  };

  const gallery = opp?.gallery || [];
  const coverId = opp?.cover_image_id;
  const cover = gallery.find((g) => g.id === coverId) || gallery[0];

  return (
    <Dialog open={!!oppId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0F17] border-slate-800 text-slate-100 max-w-4xl p-0 gap-0 overflow-hidden max-h-[90vh]" data-testid="opp-detail-dialog">
        {!opp ? (
          <div className="flex items-center justify-center h-64 text-slate-400"><Loader2 className="animate-spin mr-2" size={18}/>Carregando...</div>
        ) : (
          <div className="grid md:grid-cols-2 max-h-[90vh]">
            {/* LEFT: header + gallery + fields */}
            <div className="p-6 overflow-y-auto border-r border-[#1E293B] space-y-5">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">{opp.source}</div>
                <h2 className="font-heading text-xl font-bold mt-1" data-testid="opp-detail-title">{opp.title}</h2>
                <div className="text-sm text-slate-400">{opp.customer_name}{opp.customer_company ? " · " + opp.customer_company : ""}</div>
                {opp.customer?.whatsapp && <div className="text-xs text-slate-500 mt-0.5">WhatsApp: {opp.customer.whatsapp}</div>}
              </div>

              {/* Stage selector — funil na oportunidade; produção quando aberto pela Produção */}
              {productionItem ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">Etapa de Produção</Label>
                    <select value={prodStage} onChange={async (e) => {
                        const v = e.target.value; setProdStage(v);
                        try { const { data } = await api.patch(`/production/${productionItem.id}/stage`, { stage_key: v }); toast.success("Etapa de produção atualizada"); notifyReprint(data); onUpdated && onUpdated(); }
                        catch (err) { toast.error(err?.response?.data?.detail || "Erro ao atualizar etapa"); }
                      }} data-testid="opp-detail-prod-stage"
                      className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md">
                      {productionStages.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Prazo de entrega</Label>
                    <input type="date" value={prodDue} min={todayISO}
                      onChange={async (e) => {
                        const v = e.target.value; setProdDue(v);
                        try { const { data } = await api.patch(`/production/${productionItem.id}`, { due_date: v }); toast.success("Prazo atualizado"); notifyReprint(data); onUpdated && onUpdated(); }
                        catch (err) { toast.error(err?.response?.data?.detail || "Erro ao atualizar prazo"); setProdDue(productionItem.due_date || ""); }
                      }} data-testid="opp-detail-prod-due"
                      className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md text-slate-100"/>
                  </div>
                  {onRequestPrint && (() => { const pi = printInfo(); return (
                    <div className="col-span-2">
                      <Button onClick={onRequestPrint} title={pi?.tip}
                        className={`w-full font-semibold ${pi?.isRed ? "bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/40" : "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"}`}
                        data-testid="opp-detail-print-btn">
                        <Printer size={14} className="mr-2"/>{pi?.isRed ? "Imprimir pedido" : "Reimprimir pedido"}
                      </Button>
                      <div className="text-[11px] text-slate-500 mt-1 text-center">{pi?.tip}</div>
                    </div>
                  ); })()}
                </div>
              ) : (
                <div>
                  <Label className="text-slate-300 text-sm">Etapa do funil</Label>
                  <select value={opp.stage_key} onChange={(e) => moveStage(e.target.value)} data-testid="opp-detail-stage"
                    className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md">
                    {stages.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                  </select>
                </div>
              )}

              {/* Gallery */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-300 text-sm">Foto do produto</Label>
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="border-[#0CDBF2]/40 text-[#0CDBF2] hover:bg-[#0CDBF2]/10 h-8" data-testid="opp-upload-btn">
                    {uploading ? <Loader2 size={14} className="animate-spin mr-1"/> : <Upload size={14} className="mr-1"/>}Enviar foto
                  </Button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="opp-upload-input"/>
                </div>
                <div className="rounded-lg border border-[#1E293B] bg-[#07090E] aspect-video flex items-center justify-center overflow-hidden">
                  {cover ? (
                    <img src={mediaUrl(cover.url)} alt={cover.name} className="w-full h-full object-contain" data-testid="opp-cover-img"/>
                  ) : (
                    <div className="text-slate-600 flex flex-col items-center gap-2 text-sm"><ImageOff size={28}/>Sem foto ainda</div>
                  )}
                </div>
                {gallery.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {gallery.map((g) => (
                      <div key={g.id} className={`relative group w-16 h-16 rounded-md overflow-hidden border ${g.id === (coverId || cover?.id) ? "border-[#0CDBF2]" : "border-[#1E293B]"}`} data-testid={`opp-thumb-${g.id}`}>
                        <img src={mediaUrl(g.url)} alt={g.name} className="w-full h-full object-cover cursor-pointer" onClick={() => setCover(g.id)} />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                          <button onClick={() => setCover(g.id)} title="Definir como capa" className="text-[#0CDBF2]" data-testid={`opp-cover-${g.id}`}><Star size={14}/></button>
                          {g.type === "upload" && <button onClick={() => delImage(g.id)} title="Remover" className="text-red-400" data-testid={`opp-delimg-${g.id}`}><Trash2 size={14}/></button>}
                        </div>
                        {g.type === "mockup" && <span className="absolute bottom-0 left-0 right-0 text-[8px] text-center bg-[#0CDBF2]/80 text-[#07090E] font-semibold">IA</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Editable fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-slate-300 text-sm">Título</Label>
                  <Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="opp-f-title"/></div>
                <div><Label className="text-slate-300 text-sm">Produto</Label>
                  <Input value={form.product} onChange={(e)=>setForm({...form,product:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="opp-f-product"/></div>
                <div><Label className="text-slate-300 text-sm">Qtde *</Label>
                  <Input type="number" value={form.quantity} onChange={(e)=>setForm({...form,quantity:e.target.value})} className={`bg-[#07090E] mt-1.5 ${qtyMissing ? "border-red-500/70" : "border-slate-800"}`} data-testid="opp-f-qty"/>
                  {qtyMissing && <div className="text-[11px] text-red-400 mt-1" data-testid="opp-qty-required">Quantidade obrigatória</div>}</div>
                <div><Label className="text-slate-300 text-sm">Valor estimado (R$)</Label>
                  <Input type="number" value={form.estimated_value} onChange={(e)=>setForm({...form,estimated_value:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="opp-f-value"/></div>
                <div><Label className="text-slate-300 text-sm">Vendedor</Label>
                  <select value={form.owner_id} onChange={(e)=>setForm({...form,owner_id:e.target.value})} data-testid="opp-f-owner"
                    className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md">
                    <option value="">Sem responsável</option>
                    {sellers.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                  </select></div>
                <div className="col-span-2"><Label className="text-slate-300 text-sm">Observações</Label>
                  <Textarea value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="opp-f-notes"/></div>
              </div>

              <div className="rounded-lg border border-[#1E293B] bg-[#07090E] p-3">
                <div className="text-sm font-medium mb-2">Grade de tamanhos</div>
                <div className="grid grid-cols-12 gap-2 text-[11px] text-slate-500 mb-1 px-1">
                  <div className="col-span-3">Qtd</div><div className="col-span-4">Modelagem</div><div className="col-span-4">Tamanho</div><div className="col-span-1"></div>
                </div>
                {grid.map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`size-row-${i}`}>
                    <Input type="number" value={r.qty} onChange={(e)=>setRow(i,"qty",e.target.value)} className="col-span-3 bg-[#0B0F17] border-slate-800 h-9" data-testid={`size-qty-${i}`} />
                    <select value={r.modelagem} onChange={(e)=>setRow(i,"modelagem",e.target.value)} data-testid={`size-mod-${i}`}
                      className="col-span-4 bg-[#0B0F17] border border-slate-800 h-9 px-2 text-sm rounded-md">
                      <option value="">—</option><option>MASC</option><option>FEM</option><option>INF</option>
                    </select>
                    <select value={r.tamanho} onChange={(e)=>setRow(i,"tamanho",e.target.value)} disabled={!r.modelagem} data-testid={`size-tam-${i}`}
                      className="col-span-4 bg-[#0B0F17] border border-slate-800 h-9 px-2 text-sm rounded-md disabled:opacity-40">
                      <option value="">—</option>
                      {sizesFor(r.modelagem).map((s)=><option key={s} value={s}>{s}</option>)}
                    </select>
                    {grid.length > 1 && <button onClick={()=>rmRow(i)} className="col-span-1 flex justify-center text-red-400" data-testid={`size-rm-${i}`}><Trash2 size={14}/></button>}
                  </div>
                ))}
                {grid.length > 0 && rowDone(grid[grid.length-1]) && (
                  <Button size="sm" variant="outline" onClick={addRow} className="w-full border-[#0CDBF2]/40 text-[#0CDBF2] hover:bg-[#0CDBF2]/10 mt-1" data-testid="size-add-row">+ Liberar próxima linha</Button>
                )}
                <div className="text-xs text-slate-500 mt-2">Total de peças: <span className={`font-mono ${qtyMismatch ? "text-red-400" : "text-[#0CDBF2]"}`}>{gridTotal}</span></div>
                {qtyMismatch && (
                  <div className="mt-2 text-xs text-red-400 flex items-center gap-2 flex-wrap" data-testid="qty-mismatch-warning">
                    <AlertTriangle size={13} className="shrink-0"/>
                    <span>Total da grade ({gridTotal}) ≠ Qtde ({qtyNum}).</span>
                    <button type="button" onClick={()=>setForm((f)=>({...f, quantity: gridTotal}))} className="underline text-[#0CDBF2] font-medium" data-testid="qty-fix-btn">Ajustar Qtde para {gridTotal}</button>
                  </div>
                )}
              </div>
              <Button onClick={saveFields} disabled={saving || qtyMismatch || qtyMissing} className="w-full bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold disabled:opacity-50" data-testid="opp-save-fields">
                {saving ? <Loader2 size={14} className="animate-spin mr-2"/> : <Save size={14} className="mr-2"/>}Salvar alterações
              </Button>

              <div className="rounded-lg border border-[#1E293B] bg-[#07090E] p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm">
                    <div className="font-medium flex items-center gap-1.5"><ShoppingCart size={13} className="text-[#0CDBF2]"/>Tiny ERP</div>
                    {opp.tiny_order?.tiny_id
                      ? <div className="text-xs text-slate-400" data-testid="opp-tiny-status">Pedido nº {opp.tiny_order.tiny_numero || opp.tiny_order.tiny_id} · {opp.tiny_order.tiny_status}</div>
                      : <div className="text-xs text-slate-500">Ainda não enviado</div>}
                  </div>
                  {opp.tiny_order?.tiny_id
                    ? <Button size="sm" variant="outline" onClick={checkTiny} disabled={tinyLoading} className="border-slate-700 hover:bg-slate-800" data-testid="opp-tiny-refresh">{tinyLoading ? <Loader2 size={14} className="animate-spin"/> : "Atualizar status"}</Button>
                    : <Button size="sm" onClick={pushTiny} disabled={tinyLoading} className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="opp-tiny-push">{tinyLoading ? <Loader2 size={14} className="animate-spin"/> : "Enviar ao Tiny"}</Button>}
                </div>
              </div>
            </div>

            {/* RIGHT: activity timeline */}
            <div className="flex flex-col bg-[#0B0F17] max-h-[90vh]">
              <div className="p-4 border-b border-[#1E293B] shrink-0">
                <div className="font-heading font-semibold text-sm">Relatos & histórico</div>
                <div className="text-xs text-slate-500">{opp.owner_name ? `Responsável: ${opp.owner_name}` : "Sem responsável"} · {BRL(opp.estimated_value)}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="opp-timeline">
                {(opp.activities || []).length === 0 && <div className="text-slate-500 text-sm text-center py-8">Nenhum relato ainda.</div>}
                {(opp.activities || []).map((a) => {
                  const Icon = ACT_ICON[a.type] || StickyNote;
                  return (
                    <div key={a.id} className="flex gap-3" data-testid={`activity-${a.id}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${a.type === "ai" ? "bg-[#0CDBF2]/15 text-[#0CDBF2]" : a.type === "stage_change" ? "bg-amber-500/15 text-amber-400" : "bg-slate-700/40 text-slate-300"}`}>
                        <Icon size={13}/>
                      </div>
                      <div className="flex-1 bg-[#111622] border border-[#1E293B] rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-slate-300">{a.author_name || "Sistema"}</span>
                          <span className="text-[10px] text-slate-500">{fmtDate(a.created_at)}</span>
                        </div>
                        <div className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{a.text}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-3 border-t border-[#1E293B] shrink-0">
                <Textarea value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Escrever um relato..." rows={2}
                  className="bg-[#07090E] border-slate-800 resize-none" data-testid="opp-note-input"
                  onKeyDown={(e)=>{ if(e.key==="Enter" && (e.metaKey||e.ctrlKey)) addNote(); }}/>
                <Button onClick={addNote} disabled={!note.trim()} className="w-full mt-2 bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="opp-note-send">
                  <Send size={14} className="mr-2"/>Adicionar relato
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
